import { supabase } from '@/integrations/supabase/client';
import type { ElementContextData } from '@/types/savedReports';

export const REPORT_WRITER_SYSTEM_PROMPT = `You are an analytics report writer. Your task is to write clear, concise text for an analytics report based on the user's instructions.

If context data is provided, use it to inform your writing with specific numbers, trends, and insights from the data.

Guidelines:
- Be concise and factual
- Reference specific numbers from the context when relevant
- Write in a professional analytical style
- Do not invent data not present in the context
- Format your response using markdown for better readability
- Use **bold** for emphasis, bullet points for lists, and other markdown formatting as appropriate
- Do not use top-level headers (# or ##) - the text will appear within a report section`;

/**
 * Generate report text using AI with optional element context
 */
export async function generateReportText(
  userPrompt: string,
  elementContext?: ElementContextData
): Promise<string> {
  // Get current session token
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('No active session');
  }

  // Build the user prompt with optional context
  let fullUserPrompt = `User instructions: ${userPrompt}`;

  if (elementContext) {
    fullUserPrompt += `\n\n---\nContext from "${elementContext.label}":\n${elementContext.contextText}`;
  }

  // Call ai-composer edge function
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-composer`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        systemPrompt: REPORT_WRITER_SYSTEM_PROMPT,
        userPrompt: fullUserPrompt,
        maxTokens: 500,
        temperature: 0.7,
        useGemini: true,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`AI generation failed: ${error}`);
  }

  const result = await response.json();

  // ai-composer returns { text: string }
  const generatedText = result.text;

  if (!generatedText) {
    throw new Error('No text generated by AI');
  }

  return generatedText.trim();
}

/**
 * Helper to format element data for context display
 */
export function formatElementContextPreview(context: ElementContextData): string {
  return `${context.label}\n${context.description}`;
}
